import { useCallback, useEffect, useRef, useState } from 'react'
import { createLogger } from '@/lib/logger'
import { useSocket } from '@/lib/socket/SocketContext'
import { useOrganizationStore } from '@/store/organization.store'

import { ExtendedSocket, SocketEvent } from '@scrambl/shared'

import { realtimeStore } from '../store'

const logger = createLogger('lib/realtime/core/useRealtime')

// Add debug mode flag similar to API client
const isBrowser =
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'
const DEBUG_MODE =
  import.meta.env.DEV &&
  isBrowser &&
  localStorage.getItem('REALTIME_DEBUG') === 'true'

// Helper functions for conditional logging
function debugLog(..._args: unknown[]) {
  // Debug logging disabled - enable REALTIME_DEBUG in localStorage
}

// Helper function to generate unique message IDs
function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

// Centralized BroadcastChannel name
const APP_BROADCAST_CHANNEL = 'scrambl-realtime-sync'

// Type definitions
interface RoomOperation {
  type: 'join' | 'leave'
  roomType:
    | 'thread'
    | 'task'
    | 'project'
    | 'organization'
    | 'user'
    | 'note'
    | 'collection'
  id: string
}

interface EventPayload {
  id: string
  organizationId?: string
  threadIds?: string[]
  [key: string]: unknown
}

// Event types for cross-tab synchronization
export enum BroadcastEventType {
  THREAD_CREATED = 'thread:created',
  THREAD_UPDATED = 'thread:updated',
  THREAD_DELETED = 'thread:deleted',
  THREAD_COMMENT_CREATED = 'thread:comment_created',
  THREAD_COMMENT_UPDATED = 'thread:comment_updated',
  THREAD_COMMENT_DELETED = 'thread:comment_deleted',
  THREAD_REACTION_ADDED = 'thread:reaction_added',
  THREAD_REACTION_REMOVED = 'thread:reaction_removed',
  COMMENT_REACTION_ADDED = 'comment:reaction_added',
  COMMENT_REACTION_REMOVED = 'comment:reaction_removed',
  TASK_UPDATED = 'task:updated',
  TASK_STATUS_UPDATED = 'task:status_updated',
  PROJECT_UPDATED = 'project:updated',
  ORGANIZATION_UPDATED = 'organization:updated',
  ACTIVE_THREADS_HEARTBEAT = 'active_threads:heartbeat',
}

// Interface for broadcast messages
export interface BroadcastMessage<T = unknown> {
  type: BroadcastEventType | string
  entityType: string
  entityId: string
  data: T
  timestamp: number
  messageId: string
  source: 'socket' | 'client'
  tabId: string
}

interface BaseEventOptions {
  // Common options for all realtime hooks
  throttleEvents?: boolean
  enableBroadcastChannel?: boolean
  suppressLogging?: boolean // Add general logging suppression
}

export interface UseRealtimeOptions extends BaseEventOptions {
  // No additional options for the base hook currently
}

// Global registry of active threads/rooms being viewed by the user
// This helps optimize event processing across components
interface ActiveEntityRegistry {
  threads: Set<string>
  tasks: Set<string>
  projects: Set<string>
  organizations: Set<string>
  users: Set<string>
  notes: Set<string>
  collections: Set<string>
}

// Ensure singletons are truly global by attaching to window
declare global {
  interface Window {
    __realtimeState?: {
      activeEntities: ActiveEntityRegistry
      visibleEntities: ActiveEntityRegistry
      pendingRoomOperations: Array<{
        type: 'join' | 'leave'
        roomType: string
        id: string
      }>
      roomOperationTimer: NodeJS.Timeout | null
    }
  }
}

// Initialize or get existing state from window
if (typeof window !== 'undefined' && !window.__realtimeState) {
  window.__realtimeState = {
    activeEntities: {
      threads: new Set<string>(),
      tasks: new Set<string>(),
      projects: new Set<string>(),
      organizations: new Set<string>(),
      users: new Set<string>(),
      notes: new Set<string>(),
      collections: new Set<string>(),
    },
    visibleEntities: {
      threads: new Set<string>(),
      tasks: new Set<string>(),
      projects: new Set<string>(),
      organizations: new Set<string>(),
      users: new Set<string>(),
      notes: new Set<string>(),
      collections: new Set<string>(),
    },
    pendingRoomOperations: [],
    roomOperationTimer: null,
  }
}

// CRITICAL: Get references to the SAME objects from window state
// Do NOT create new objects, use the existing ones
const getRealtimeState = () => {
  if (typeof window === 'undefined') {
    return {
      activeEntities: {
        threads: new Set<string>(),
        tasks: new Set<string>(),
        projects: new Set<string>(),
        organizations: new Set<string>(),
        users: new Set<string>(),
        notes: new Set<string>(),
        collections: new Set<string>(),
      },
      visibleEntities: {
        threads: new Set<string>(),
        tasks: new Set<string>(),
        projects: new Set<string>(),
        organizations: new Set<string>(),
        users: new Set<string>(),
        notes: new Set<string>(),
        collections: new Set<string>(),
      },
      pendingRoomOperations: [],
      roomOperationTimer: null,
    }
  }

  // Ensure window state exists
  if (!window.__realtimeState) {
    window.__realtimeState = {
      activeEntities: {
        threads: new Set<string>(),
        tasks: new Set<string>(),
        projects: new Set<string>(),
        organizations: new Set<string>(),
        users: new Set<string>(),
        notes: new Set<string>(),
        collections: new Set<string>(),
      },
      visibleEntities: {
        threads: new Set<string>(),
        tasks: new Set<string>(),
        projects: new Set<string>(),
        organizations: new Set<string>(),
        users: new Set<string>(),
        notes: new Set<string>(),
        collections: new Set<string>(),
      },
      pendingRoomOperations: [],
      roomOperationTimer: null,
    }
  }

  return window.__realtimeState
}

// Create local state references that will be managed by this hook
// We'll use the realtimeStore for actual persistence
const activeEntities = {
  threads: new Set<string>(),
  tasks: new Set<string>(),
  projects: new Set<string>(),
  organizations: new Set<string>(),
  users: new Set<string>(),
  notes: new Set<string>(),
  collections: new Set<string>(),
}

const visibleEntities = {
  threads: new Set<string>(),
  tasks: new Set<string>(),
  projects: new Set<string>(),
  organizations: new Set<string>(),
  users: new Set<string>(),
  notes: new Set<string>(),
  collections: new Set<string>(),
}

const pendingRoomOperations: any[] = []
let roomOperationTimer: NodeJS.Timeout | null = null

// Helper functions for timer management
const getRoomOperationTimer = () => roomOperationTimer
const setRoomOperationTimer = (timer: NodeJS.Timeout | null) => {
  roomOperationTimer = timer
}

// Deferred room joins that were requested before the socket was ready.
// Keyed by `${roomType}:${id}` so a leave (or a duplicate join) can find and
// mutate the exact pending entry. These are flushed once the socket connects.
// `organizationId` is only meaningful for thread joins (needed by the
// server-side batch_join_threads room grouping).
interface DeferredJoin {
  roomType: string
  id: string
  organizationId?: string
}
const deferredJoins = new Map<string, DeferredJoin>()

const deferredJoinKey = (roomType: string, id: string) => `${roomType}:${id}`

// Map a room type to its entry in the active-entity registry. Returns null for
// room types (e.g. 'user') that are not tracked in the registry.
const roomTypeToRegistryKey = (
  roomType: string,
): keyof ActiveEntityRegistry | null => {
  switch (roomType) {
    case 'thread':
      return 'threads'
    case 'task':
      return 'tasks'
    case 'project':
      return 'projects'
    case 'organization':
      return 'organizations'
    case 'user':
      return 'users'
    case 'note':
      return 'notes'
    case 'collection':
      return 'collections'
    default:
      return null
  }
}

// Record a room membership we could not emit yet. Idempotent: re-recording the
// same room just refreshes the entry.
const recordDeferredJoin = (join: DeferredJoin) => {
  deferredJoins.set(deferredJoinKey(join.roomType, join.id), join)
}

// Drop a deferred join so a leave-before-connect doesn't ghost-join on flush.
const removeDeferredJoin = (roomType: string, id: string) => {
  deferredJoins.delete(deferredJoinKey(roomType, id))
}

// Emit a single room join directly on the socket and reconcile membership
// state on ack. Shared by the deferred-join flush and the reconnect rejoin so
// both paths stay in lockstep with the batched happy path in
// processBatchedRoomOperations. A re-emit for an already-joined room is
// harmless (the server treats join as idempotent).
const emitJoinRoom = (socket: ExtendedSocket, roomType: string, id: string) => {
  let eventName: string
  const payload: EventPayload = { id }

  switch (roomType) {
    case 'thread':
      eventName = SocketEvent.JOIN_THREAD
      ;(payload as any).threadId = id
      break
    case 'organization':
      eventName = 'join_organization'
      ;(payload as any).organizationId = id
      break
    case 'project':
      eventName = SocketEvent.JOIN_PROJECT
      ;(payload as any).projectId = id
      break
    case 'task':
      eventName = 'join_task'
      ;(payload as any).taskId = id
      break
    case 'user':
      eventName = 'join_user'
      ;(payload as any).userId = id
      break
    case 'note':
      eventName = SocketEvent.JOIN_NOTE
      ;(payload as any).noteId = id
      break
    case 'collection':
      eventName = SocketEvent.JOIN_COLLECTION
      ;(payload as any).collectionId = id
      break
    default:
      logger.warn(`[useRealtime] Unknown room type for join: ${roomType}`)
      return
  }

  const storeKey = (roomType + 's') as keyof typeof realtimeStore.store.rooms
  socket.emit(eventName, payload, (response: any) => {
    if (response?.success) {
      realtimeStore.markJoined(storeKey, id)
    } else {
      realtimeStore.markLeft(storeKey, id)
    }
    if (roomType === 'thread') {
      const logData = {
        threadId: id,
        socketId: socket?.id ?? null,
        response: response ?? null,
      }
      if (response?.success) {
        logger.info('[useRealtime] Deferred/rejoin thread room ack', logData)
      } else {
        logger.warn(
          '[useRealtime] Deferred/rejoin thread room ack failed',
          logData,
        )
      }
    }
  })
}

// Re-emit joins for every room the client believes it is in. The server drops
// room membership on a fresh connection, so this must run on every 'connect'
// (including reconnects) to restore live delivery. Also flushes joins that
// were deferred because the socket wasn't ready when the caller asked.
const flushRoomJoinsOnConnect = (socket: ExtendedSocket) => {
  // 1. Flush deferred joins recorded while the socket was not ready. Group
  //    thread joins so they ride the server-side batch join path.
  const deferredThreadsByOrg = new Map<string, string[]>()
  deferredJoins.forEach(join => {
    if (join.roomType === 'thread') {
      const orgId = join.organizationId ?? ''
      const list = deferredThreadsByOrg.get(orgId) ?? []
      list.push(join.id)
      deferredThreadsByOrg.set(orgId, list)
    } else {
      emitJoinRoom(socket, join.roomType, join.id)
    }
  })
  deferredThreadsByOrg.forEach((threadIds, orgId) => {
    if (orgId) {
      socket.emit('batch_join_threads', { threadIds, organizationId: orgId })
    } else {
      // No org context was captured — fall back to individual joins.
      threadIds.forEach(threadId => emitJoinRoom(socket, 'thread', threadId))
    }
    for (const threadId of threadIds) {
      activeEntities.threads.add(threadId)
    }
  })
  deferredJoins.clear()

  // 2. Re-emit joins for rooms we consider active. On a reconnect the server
  //    has forgotten them; on a first connect this is a harmless no-op set.
  const rejoinEntityTypes: Array<{
    registryKey: keyof ActiveEntityRegistry
    roomType: string
  }> = [
    { registryKey: 'threads', roomType: 'thread' },
    { registryKey: 'tasks', roomType: 'task' },
    { registryKey: 'projects', roomType: 'project' },
    { registryKey: 'organizations', roomType: 'organization' },
    { registryKey: 'notes', roomType: 'note' },
    { registryKey: 'collections', roomType: 'collection' },
  ]
  rejoinEntityTypes.forEach(({ registryKey, roomType }) => {
    activeEntities[registryKey].forEach(id => {
      emitJoinRoom(socket, roomType, id)
    })
  })
}

// Track socket readiness status globally across hook instances
const socketInitialized = {
  status: false,
  lastChecked: 0,
  readyLogEmitted: false, // Track if "Socket is now ready" was already logged
}
const lastDeltaRunAt: { ts: number } = { ts: 0 }

// The hook mounts once per realtime consumer, so guard the 'connect' handler
// that flushes deferred joins / rejoins active rooms to a single registration
// keyed by socket instance. Without this every hook instance would attach its
// own listener and we'd emit N duplicate joins per room on reconnect.
let connectHandlerBoundSocket: unknown = null

// Generate a unique ID for this tab
const TAB_ID = Math.random().toString(36).substring(2, 15)

// Export utility functions for checking entity status
export const isEntityActive = (
  entityType: keyof ActiveEntityRegistry,
  entityId: string,
): boolean => {
  return activeEntities[entityType].has(entityId)
}

export const isEntityVisible = (
  entityType: keyof ActiveEntityRegistry,
  entityId: string,
): boolean => {
  return visibleEntities[entityType].has(entityId)
}

/**
 * Validates if an entity belongs to the expected organization context
 */
export const validateOrganizationMatch = (
  data: { organizationId?: string },
  expectedOrgId?: string | null,
): boolean => {
  if (!expectedOrgId || !data) return true

  // If data contains organizationId, check if it matches expected org
  if ('organizationId' in data) {
    return data.organizationId === expectedOrgId
  }

  return true
}

/**
 * Core real-time hook that manages socket connections and room management
 * This serves as the foundation for all feature-specific real-time hooks
 */
export function useRealtime(options: UseRealtimeOptions = {}) {
  const { socket: rawSocket, isConnected, socketId } = useSocket()
  const socket = rawSocket as unknown as ExtendedSocket | null
  const { currentOrganization } = useOrganizationStore()
  const [isReady, setIsReady] = useState(false)
  const connectionAttempts = useRef(0)
  const deltaRanRef = useRef(false)

  // Keep track of current organization for event filtering
  const currentOrgIdRef = useRef<string | null>(null)

  // Track processed events with timestamps to prevent duplicates
  const processedEventsRef = useRef<Map<string, number>>(new Map())

  // Centralized BroadcastChannel reference
  const channelRef = useRef<BroadcastChannel | null>(null)

  // Store event listeners
  const eventListenersRef = useRef<Map<string, Set<(data: unknown) => void>>>(
    new Map(),
  )

  // Update org ref when current organization changes
  useEffect(() => {
    currentOrgIdRef.current = currentOrganization?.id || null
  }, [currentOrganization])

  // Track socket initialization and readiness
  useEffect(() => {
    // Check if socket is ready
    if (isConnected && socket) {
      // Socket is connected
      socketInitialized.status = true
      socketInitialized.lastChecked = Date.now()

      if (!isReady) {
        // Only log once per page load, but always log for debugging navigation issues
        if (!socketInitialized.readyLogEmitted) {
          debugLog('[useRealtime] Socket is now ready')
          socketInitialized.readyLogEmitted = true
        }
        setIsReady(true)
        const deltaEnabled = false // default off to prevent background API activity
        if (deltaEnabled && !deltaRanRef.current) {
          deltaRanRef.current = true
          ;(async () => {
            try {
              // Simple throttle: do not run more than once every 60s across reconnect flaps
              if (Date.now() - lastDeltaRunAt.ts < 60000) return
              lastDeltaRunAt.ts = Date.now()
              // On reconnect, invalidate entity queries to trigger refresh
              // Entities are managed by TanStack Query - cache invalidation handles reconciliation
              const { useQueryClient } = await import('@tanstack/react-query')
            } catch (e) {
              // ignore dynamic import issues
            }
          })()
        }
      }
    } else if (socket && !isConnected) {
      // Socket exists but not connected yet
      if (connectionAttempts.current < 5) {
        connectionAttempts.current++
        debugLog(
          `[useRealtime] Waiting for socket connection, attempt ${connectionAttempts.current}`,
        )
      }
    } else {
      // No socket yet
      if (isReady) {
        debugLog(
          '[useRealtime] Socket disconnected during navigation - attempting to maintain connection',
        )
        // Don't immediately set to false during navigation - give it time to reconnect
        setTimeout(() => {
          if (!isConnected && !socket) {
            setIsReady(false)
            socketInitialized.readyLogEmitted = false
            deltaRanRef.current = false
          }
        }, 1000) // Wait 1 second before marking as disconnected
      }
      socketInitialized.status = false
    }
  }, [socket, isConnected, isReady])

  // Bind a single 'connect' handler per socket instance that flushes deferred
  // joins and rejoins active rooms. The hook mounts many times, so we guard on
  // a module-level marker keyed by the socket instance to avoid N duplicate
  // handlers (and therefore N duplicate joins on every reconnect). socket.io
  // fires 'connect' on both the initial connect and every reconnect, so this
  // covers the reconnect rejoin case even though the socket instance is reused.
  //
  // We intentionally do NOT remove the handler when this particular hook
  // instance unmounts: it belongs to the socket instance, not to the hook, and
  // other consumers still depend on it. The handler is discarded when the
  // socket instance is torn down (SocketContext calls removeAllListeners), and
  // rebinding happens only when the socket instance itself changes.
  useEffect(() => {
    if (!socket) return
    if (connectHandlerBoundSocket === socket) return
    connectHandlerBoundSocket = socket

    socket.on('connect', () => {
      flushRoomJoinsOnConnect(socket)
    })

    // If we bound after the socket already connected, flush now so a comments
    // panel that mounted just after connect (but recorded a deferred join
    // before this handler existed) still gets its room.
    if (isConnected) {
      flushRoomJoinsOnConnect(socket)
    }
  }, [socket, isConnected])

  // Initialize the BroadcastChannel for cross-tab communication
  useEffect(() => {
    // Skip if BroadcastChannel is not supported or disabled
    if (
      typeof BroadcastChannel === 'undefined' ||
      options.enableBroadcastChannel === false
    ) {
      return
    }

    try {
      // Create a single BroadcastChannel for all realtime events
      channelRef.current = new BroadcastChannel(APP_BROADCAST_CHANNEL)

      // Set up message handler for receiving events from other tabs
      const handleMessage = (event: MessageEvent) => {
        const message = event.data as BroadcastMessage

        // Ignore messages from this tab
        if (message.tabId === TAB_ID) {
          return
        }

        // Create a unique event ID for deduplication
        const eventId =
          message.messageId ||
          `${message.type}-${message.entityId}-${message.timestamp}`

        // Skip if already processed recently
        if (hasProcessedEvent(eventId)) {
          return
        }

        // Mark as processed to prevent duplicate handling
        markEventProcessed(eventId)

        debugLog(
          `[useRealtime] Received broadcast message: ${message.type} for ${message.entityType}:${message.entityId}`,
        )

        // Notify all registered listeners for this event type
        notifyEventListeners(message.type, message.data)
      }

      // Listen for messages from other tabs
      channelRef.current.addEventListener('message', handleMessage)

      if (!options.suppressLogging) {
        debugLog('[Realtime] BroadcastChannel initialized')
      }

      // Cleanup function
      return () => {
        if (channelRef.current) {
          channelRef.current.removeEventListener('message', handleMessage)
          channelRef.current.close()
          channelRef.current = null
          debugLog('[useRealtime] BroadcastChannel closed')
        }
      }
    } catch (error) {
      logger.error('[useRealtime] Error initializing BroadcastChannel:', error)
      return () => {}
    }
  }, [options.enableBroadcastChannel])

  // Notify all event listeners for a given event type
  const notifyEventListeners = useCallback(
    (eventType: string, data: unknown) => {
      const listeners = eventListenersRef.current.get(eventType)
      if (listeners) {
        listeners.forEach(listener => {
          listener(data)
        })
      }
    },
    [],
  )

  // Register an event listener
  const addEventListener = useCallback(
    (eventType: string, callback: (data: unknown) => void) => {
      // Get or create the set of listeners for this event type
      const listeners = eventListenersRef.current.get(eventType) || new Set()
      listeners.add(callback)
      eventListenersRef.current.set(eventType, listeners)

      // Return cleanup function
      return () => {
        const listeners = eventListenersRef.current.get(eventType)
        if (listeners) {
          listeners.delete(callback)
          if (listeners.size === 0) {
            eventListenersRef.current.delete(eventType)
          }
        }
      }
    },
    [],
  )

  // Clean up processed events periodically to prevent memory leaks
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now()

      // Remove entries older than 5 minutes
      for (const [key, timestamp] of processedEventsRef.current.entries()) {
        if (now - timestamp > 300000) {
          // 5 minutes
          processedEventsRef.current.delete(key)
        }
      }
    }, 60000) // Clean every minute

    return () => clearInterval(cleanupInterval)
  }, [])

  /**
   * Utility method to mark an event as processed to prevent duplication
   */
  const markEventProcessed = useCallback(
    (eventId: string, expiresInMs = 5000) => {
      processedEventsRef.current.set(eventId, Date.now())

      // Auto-cleanup after expiration
      if (expiresInMs > 0) {
        setTimeout(() => {
          processedEventsRef.current.delete(eventId)
        }, expiresInMs)
      }
    },
    [],
  )

  /**
   * Check if an event has already been processed
   */
  const hasProcessedEvent = useCallback(
    (eventId: string, withinMs = 5000): boolean => {
      const timestamp = processedEventsRef.current.get(eventId)
      if (!timestamp) return false

      // Check if the event was processed within the specified time window
      return Date.now() - timestamp < withinMs
    },
    [],
  )

  /**
   * Broadcast a message to other tabs
   */
  const broadcastToTabs = useCallback((message: BroadcastMessage) => {
    if (!channelRef.current) return
    channelRef.current.postMessage(message)
  }, [])

  /**
   * Process a socket message and broadcast it to other tabs if needed
   * This function should be called when a socket message is received
   */
  const processSocketEvent = useCallback(
    <T>(eventType: string, entityType: string, entityId: string, data: T) => {
      const eventKey = `${eventType}-${entityId}`

      // Skip if we've already processed this message
      if (hasProcessedEvent(eventKey)) {
        debugLog(`[useRealtime] Skipping duplicate message: ${eventType}`)
        return
      }

      // Mark message as processed
      markEventProcessed(eventKey)

      // Notify listeners
      notifyEventListeners(eventType, data)

      // If this is a deletion message, ensure it's not re-broadcast endlessly
      const typedData = data as { _source?: 'socket' | 'client' | 'broadcast' }
      if (eventType.includes('deleted') && typedData._source === 'broadcast') {
        debugLog(
          `[useRealtime] Skipping re-broadcast of deletion message: ${eventType}`,
        )
        return
      }

      // Broadcast to other tabs - preserve _source if it exists, otherwise use 'socket'
      const source =
        typedData._source === 'socket' || typedData._source === 'client'
          ? typedData._source
          : 'socket'
      broadcastToTabs({
        type: eventType,
        entityType,
        entityId,
        data,
        timestamp: Date.now(),
        messageId: generateMessageId(),
        source,
        tabId: TAB_ID,
      })
    },
    [
      hasProcessedEvent,
      markEventProcessed,
      notifyEventListeners,
      broadcastToTabs,
    ],
  )

  // Process room operations
  const processBatchedRoomOperations = useCallback(() => {
    // Collapse rapid join/leave churn per room. The last queued operation is
    // the only one that should hit the socket, otherwise open+close can leave
    // the client subscribed by processing leave before a stale join.
    const latestOperationByRoom = new Map<string, RoomOperation>()

    pendingRoomOperations.forEach(op => {
      latestOperationByRoom.set(`${op.roomType}:${op.id}`, {
        type: op.type,
        roomType: op.roomType as RoomOperation['roomType'],
        id: op.id,
      })
    })

    const finalOperations = Array.from(latestOperationByRoom.values())
    const joinOperations = finalOperations.filter(op => op.type === 'join')
    const leaveOperations = finalOperations.filter(op => op.type === 'leave')

    if (joinOperations.length > 0 || leaveOperations.length > 0) {
      debugLog(`[useRealtime] Processing room operations:`, {
        joins: joinOperations,
        leaves: leaveOperations,
      })
    }

    const threadJoinCount = joinOperations.filter(
      op => op.roomType === 'thread',
    ).length
    const threadLeaveCount = leaveOperations.filter(
      op => op.roomType === 'thread',
    ).length
    if (threadJoinCount > 0 || threadLeaveCount > 0) {
      logger.info('[useRealtime] Processing batched thread room operations', {
        socketId: socket?.id ?? null,
        threadJoinCount,
        threadLeaveCount,
        pendingOperationCount: joinOperations.length + leaveOperations.length,
      })
    }

    // Process individual operations
    const individualJoins = joinOperations.filter(op =>
      ['thread', 'organization', 'project', 'task', 'user', 'note'].includes(
        op.roomType,
      ),
    )

    const individualLeaves = leaveOperations.filter(op =>
      ['thread', 'organization', 'project', 'task', 'user', 'note'].includes(
        op.roomType,
      ),
    )

    // Process individual leave operations
    individualLeaves.forEach(op => {
      if (!socket) return

      let eventName: string
      const payload: EventPayload = { id: op.id }

      switch (op.roomType) {
        case 'thread':
          eventName = SocketEvent.LEAVE_THREAD
          ;(payload as any).threadId = op.id
          break
        case 'organization':
          eventName = 'leave_organization'
          ;(payload as any).organizationId = op.id
          break
        case 'project':
          eventName = 'leave_project'
          ;(payload as any).projectId = op.id
          break
        case 'task':
          eventName = 'leave_task'
          ;(payload as any).taskId = op.id
          break
        case 'user':
          eventName = 'leave_user'
          ;(payload as any).userId = op.id
          break
        case 'note':
          eventName = SocketEvent.LEAVE_NOTE
          ;(payload as any).noteId = op.id
          break
        default:
          return
      }

      const storeKey = (op.roomType +
        's') as keyof typeof realtimeStore.store.rooms
      socket.emit(eventName, payload, (response: any) => {
        // Clear local membership state even if the server already forgot this room.
        if (response?.success || response === undefined) {
          realtimeStore.markLeft(storeKey, op.id)
        }
        if (op.roomType === 'thread') {
          logger.info('[useRealtime] Leave thread room ack', {
            threadId: op.id,
            socketId: socket?.id ?? null,
            response: response ?? null,
          })
        }
      })
    })

    // Process individual join operations
    individualJoins.forEach(op => {
      if (!socket) return

      let eventName: string
      const payload: EventPayload = { id: op.id }

      switch (op.roomType) {
        case 'thread':
          eventName = SocketEvent.JOIN_THREAD
          ;(payload as any).threadId = op.id
          debugLog(
            `[useRealtime] Emitting ${SocketEvent.JOIN_THREAD} for ${op.id}`,
          )
          break
        case 'organization':
          eventName = 'join_organization'
          ;(payload as any).organizationId = op.id
          break
        case 'project':
          eventName = SocketEvent.JOIN_PROJECT
          ;(payload as any).projectId = op.id
          debugLog(
            `[useRealtime] Emitting ${SocketEvent.JOIN_PROJECT} for ${op.id}`,
          )
          break
        case 'task':
          eventName = 'join_task'
          ;(payload as any).taskId = op.id
          debugLog(`[useRealtime] Emitting join_task for ${op.id}`)
          break
        case 'user':
          eventName = 'join_user'
          ;(payload as any).userId = op.id
          break
        case 'note':
          eventName = SocketEvent.JOIN_NOTE
          ;(payload as any).noteId = op.id
          debugLog(
            `[useRealtime] Emitting ${SocketEvent.JOIN_NOTE} for ${op.id}`,
          )
          break
        case 'collection':
          eventName = SocketEvent.JOIN_COLLECTION
          ;(payload as any).collectionId = op.id
          debugLog(
            `[useRealtime] Emitting ${SocketEvent.JOIN_COLLECTION} for ${op.id}`,
          )
          break
        default:
          logger.warn(`[useRealtime] Unknown room type: ${op.roomType}`)
          return
      }

      debugLog(`[useRealtime] Emitting ${eventName} with payload:`, payload)
      socket.emit(eventName, payload, (response: any) => {
        debugLog(`[useRealtime] Response from ${eventName}:`, response)
        const storeKey = (op.roomType +
          's') as keyof typeof realtimeStore.store.rooms
        // Mark as joined after successful response.
        // On failure, clear the joining state so a future reconnect can retry.
        if (response?.success) {
          realtimeStore.markJoined(storeKey, op.id)
        } else {
          realtimeStore.markLeft(storeKey, op.id)
        }
        if (op.roomType === 'thread') {
          const logData = {
            threadId: op.id,
            socketId: socket?.id ?? null,
            response: response ?? null,
          }
          if (response?.success) {
            logger.info('[useRealtime] Join thread room ack', logData)
          } else {
            logger.warn('[useRealtime] Join thread room ack failed', logData)
          }
        }
      })
    })

    // Clear pending operations
    pendingRoomOperations.length = 0
    const timer = getRoomOperationTimer()
    if (timer) {
      clearTimeout(timer)
      setRoomOperationTimer(null)
    }
  }, [socket])

  /**
   * Join a socket.io room.
   *
   * Returns true when the join was emitted OR queued. If the socket is not
   * ready yet, the membership is recorded in a module-level deferred registry
   * and flushed automatically once the socket connects, so callers never need
   * to retry. A `true` return therefore means "membership is tracked", not
   * necessarily "already joined server-side".
   */
  const joinRoom = useCallback(
    (roomType: string, id: string, isVisible: boolean = false): boolean => {
      debugLog(
        `[useRealtime.joinRoom] Called with roomType: ${roomType}, id: ${id}`,
      )

      if (!isReady || !socket) {
        debugLog(
          `[useRealtime] Deferring join ${roomType}:${id} - socket not ready`,
        )
        // User rooms are auto-joined by the backend on connect, so there's
        // nothing to defer for them.
        if (roomType !== 'user') {
          recordDeferredJoin({ roomType, id })
          // Track membership now so store-side guards (isEntityActive) pass and
          // the flush handler has the room in activeEntities to rejoin.
          const registryKey = roomTypeToRegistryKey(roomType)
          if (registryKey) {
            activeEntities[registryKey].add(id)
            if (typeof window !== 'undefined' && window.__realtimeState) {
              window.__realtimeState.activeEntities = activeEntities
            }
          }
        }
        if (roomType === 'thread') {
          logger.info('[useRealtime] Thread join deferred - socket not ready', {
            threadId: id,
            isReady,
            hasSocket: Boolean(socket),
            socketId: socket?.id ?? null,
          })
        }
        // Queued, not dropped — treat as success so callers don't retry.
        return roomType !== 'user'
      }

      try {
        // Map room type to store key
        const storeKey = (roomType +
          's') as keyof typeof realtimeStore.store.rooms

        // Check if already joined or joining
        if (realtimeStore.hasJoined(storeKey, id)) {
          debugLog(`[useRealtime] Already joined ${roomType}:${id}`)
          if (roomType === 'thread') {
            logger.info(
              '[useRealtime] Thread already joined, skipping duplicate join',
              {
                threadId: id,
                socketId: socket?.id ?? null,
              },
            )
          }
          return true
        }

        if (realtimeStore.isJoining(storeKey, id)) {
          debugLog(`[useRealtime] Already joining ${roomType}:${id}`)
          if (roomType === 'thread') {
            logger.info('[useRealtime] Thread join already in progress', {
              threadId: id,
              socketId: socket?.id ?? null,
            })
          }
          return false
        }

        // Mark as joining
        realtimeStore.markJoining(storeKey, id)

        // Track membership in the active-entity registry so store-side guards
        // (isEntityActive) pass and the connect handler can rejoin this room
        // after a reconnect. The socket forgets rooms on reconnect, so every
        // room we join must be discoverable here. (batchJoinThreads populates
        // this too; this covers the single-join path.)
        const registryKey = roomTypeToRegistryKey(roomType)
        if (registryKey && roomType !== 'user') {
          activeEntities[registryKey].add(id)
          if (typeof window !== 'undefined' && window.__realtimeState) {
            window.__realtimeState.activeEntities = activeEntities
          }
        }

        // For user rooms, don't add to pending operations since users are automatically
        // joined to their user room by the backend when they connect
        if (roomType !== 'user') {
          // A prior deferred entry (if any) is now being emitted directly.
          removeDeferredJoin(roomType, id)
          // Add to pending operations queue
          pendingRoomOperations.push({
            type: 'join',
            roomType,
            id,
          })
          if (roomType === 'thread') {
            logger.info('[useRealtime] Thread join queued', {
              threadId: id,
              queueSize: pendingRoomOperations.length,
              socketId: socket?.id ?? null,
            })
          }

          // Schedule batch processing if not already scheduled
          const timer = getRoomOperationTimer()
          if (!timer) {
            debugLog(
              `[useRealtime] Scheduling batch processing for ${pendingRoomOperations.length} operations`,
            )
            const newTimer = setTimeout(
              () => processBatchedRoomOperations(),
              50,
            ) // 50ms batch window
            setRoomOperationTimer(newTimer)
          } else {
            debugLog(
              `[useRealtime] Batch processing already scheduled, added to queue (${pendingRoomOperations.length} operations)`,
            )
          }
        }

        // Broadcast active entity to other tabs (for cross-tab awareness)
        if (roomType === 'thread') {
          broadcastToTabs({
            type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
            entityType: 'thread',
            entityId: id,
            data: { active: true, threadId: id, timestamp: Date.now() },
            timestamp: Date.now(),
            messageId: generateMessageId(),
            source: 'socket',
            tabId: TAB_ID,
          })
        }

        return true
      } catch (error) {
        logger.error(`[useRealtime] Error joining room: ${error}`)
        return false
      }
    },
    [
      isReady,
      isConnected,
      socket,
      currentOrgIdRef,
      options.suppressLogging,
      broadcastToTabs,
      processBatchedRoomOperations,
    ],
  )

  /**
   * Leave a socket.io room
   */
  const leaveRoom = useCallback(
    (roomType: string, id: string): boolean => {
      if (!isReady || !socket) {
        debugLog(
          `[useRealtime] Cannot leave ${roomType}:${id} - socket not ready`,
        )
        // A leave issued before connect must cancel any deferred join and drop
        // the tracked membership, otherwise the connect flush would ghost-join
        // a room the caller already left.
        removeDeferredJoin(roomType, id)
        const registryKey = roomTypeToRegistryKey(roomType)
        if (registryKey) {
          activeEntities[registryKey].delete(id)
          visibleEntities[registryKey].delete(id)
          if (typeof window !== 'undefined' && window.__realtimeState) {
            window.__realtimeState.activeEntities = activeEntities
            window.__realtimeState.visibleEntities = visibleEntities
          }
        }
        return false
      }

      try {
        let registryKey: keyof ActiveEntityRegistry

        // Map room type to registry key
        switch (roomType) {
          case 'thread':
            registryKey = 'threads'
            break
          case 'task':
            registryKey = 'tasks'
            break
          case 'project':
            registryKey = 'projects'
            break
          case 'organization':
            // Use dedicated organizations registry for organization events
            registryKey = 'organizations'
            break
          case 'user':
            registryKey = 'users'
            break
          case 'note':
            registryKey = 'notes'
            break
          case 'collection':
            registryKey = 'collections'
            break
          default:
            logger.error(`[useRealtime] Unknown room type: ${roomType}`)
            return false
        }

        // Ensure the registry has a Set (in case it was corrupted)
        if (
          !activeEntities[registryKey] ||
          !(activeEntities[registryKey] instanceof Set)
        ) {
          debugLog(
            `[useRealtime] Registry ${registryKey} was not a Set in leaveRoom, reinitializing`,
          )
          activeEntities[registryKey] = new Set<string>()
        }
        if (
          !visibleEntities[registryKey] ||
          !(visibleEntities[registryKey] instanceof Set)
        ) {
          visibleEntities[registryKey] = new Set<string>()
        }

        // Remove from the active and visible registries
        activeEntities[registryKey].delete(id)
        visibleEntities[registryKey].delete(id)
        // Drop any not-yet-flushed deferred join for this room too.
        removeDeferredJoin(roomType, id)

        // Update window state to persist
        if (typeof window !== 'undefined' && window.__realtimeState) {
          window.__realtimeState.activeEntities = activeEntities
          window.__realtimeState.visibleEntities = visibleEntities
        }

        // For user rooms, don't add to pending operations since users remain
        // in their user room for the duration of their connection
        if (roomType !== 'user') {
          const storeKey = (roomType +
            's') as keyof typeof realtimeStore.store.rooms
          realtimeStore.markLeft(storeKey, id)

          // Add to pending operations queue
          pendingRoomOperations.push({
            type: 'leave',
            roomType,
            id,
          })
          if (roomType === 'thread') {
            logger.info('[useRealtime] Thread leave queued', {
              threadId: id,
              queueSize: pendingRoomOperations.length,
              socketId: socket?.id ?? null,
            })
          }

          // Schedule batch processing if not already scheduled
          const timer = getRoomOperationTimer()
          if (!timer) {
            debugLog(
              `[useRealtime] Scheduling batch processing for ${pendingRoomOperations.length} operations`,
            )
            const newTimer = setTimeout(
              () => processBatchedRoomOperations(),
              50,
            ) // 50ms batch window
            setRoomOperationTimer(newTimer)
          } else {
            debugLog(
              `[useRealtime] Batch processing already scheduled, added to queue (${pendingRoomOperations.length} operations)`,
            )
          }
        }

        // Broadcast inactive entity to other tabs
        if (roomType === 'thread') {
          broadcastToTabs({
            type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
            entityType: 'thread',
            entityId: id,
            data: { active: false, threadId: id, timestamp: Date.now() },
            timestamp: Date.now(),
            messageId: generateMessageId(),
            source: 'socket',
            tabId: TAB_ID,
          })
        }

        return true
      } catch (error) {
        logger.error(`[useRealtime] Error leaving room: ${error}`)
        return false
      }
    },
    [
      isReady,
      isConnected,
      socket,
      currentOrgIdRef,
      broadcastToTabs,
      processBatchedRoomOperations,
    ],
  )

  /**
   * Join multiple thread rooms at once with a single parent log entry
   * More efficient than calling joinRoom multiple times.
   *
   * Returns true when the join was emitted OR queued. If the socket is not
   * ready, the thread memberships (with their organizationId) are recorded in
   * the deferred registry and flushed via the server-side batch join once the
   * socket connects, so callers never need to retry.
   */
  const batchJoinThreads = useCallback(
    (threadIds: string[], organizationId: string) => {
      if (!threadIds.length) {
        return false
      }

      if (!isReady || !socket) {
        debugLog(`[useRealtime] Deferring batch join threads - socket not ready`)
        // Record each thread so the connect flush re-emits them (grouped by org
        // into batch_join_threads). Track active membership now so store guards
        // pass immediately.
        for (const threadId of threadIds) {
          recordDeferredJoin({ roomType: 'thread', id: threadId, organizationId })
          activeEntities.threads.add(threadId)
        }
        if (typeof window !== 'undefined' && window.__realtimeState) {
          window.__realtimeState.activeEntities = activeEntities
        }
        return true
      }

      try {
        // Add all threads to active registry
        for (const threadId of threadIds) {
          activeEntities.threads.add(threadId)
        }

        // Log only once for all threads
        debugLog(`[useRealtime] Batch joining ${threadIds.length} thread rooms`)

        socket.emit('batch_join_threads', {
          threadIds,
          organizationId,
        })

        debugLog(
          `[useRealtime] Used server-side batch join for ${threadIds.length} threads`,
        )

        // Broadcast active threads to other tabs
        for (const threadId of threadIds) {
          broadcastToTabs({
            type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
            entityType: 'thread',
            entityId: threadId,
            data: { active: true, threadId, timestamp: Date.now() },
            timestamp: Date.now(),
            messageId: generateMessageId(),
            source: 'socket',
            tabId: TAB_ID,
          })
        }

        return true
      } catch (error) {
        logger.error(`[useRealtime] Error batch joining thread rooms: ${error}`)
        return false
      }
    },
    [isReady, socket, broadcastToTabs],
  )

  /**
   * Leave multiple thread rooms at once
   */
  const batchLeaveThreads = useCallback(
    (threadIds: string[]) => {
      if (!threadIds.length) {
        return false
      }

      if (!isReady || !socket) {
        debugLog(`[useRealtime] Cannot batch leave threads - socket not ready`)
        // Cancel any deferred joins and drop tracked membership so the connect
        // flush doesn't ghost-join threads that were already left.
        for (const threadId of threadIds) {
          removeDeferredJoin('thread', threadId)
          activeEntities.threads.delete(threadId)
          visibleEntities.threads.delete(threadId)
        }
        if (typeof window !== 'undefined' && window.__realtimeState) {
          window.__realtimeState.activeEntities = activeEntities
          window.__realtimeState.visibleEntities = visibleEntities
        }
        return false
      }

      try {
        // Remove all threads from registries
        for (const threadId of threadIds) {
          removeDeferredJoin('thread', threadId)
          activeEntities.threads.delete(threadId)
          visibleEntities.threads.delete(threadId)
        }

        // Log only once for all threads
        debugLog(`[useRealtime] Batch leaving ${threadIds.length} thread rooms`)

        // Check if server supports batch leave
        if (
          socket.hasListeners &&
          typeof socket.hasListeners === 'function' &&
          socket.hasListeners('batch_leave_threads')
        ) {
          socket.emit('batch_leave_threads', { threadIds })

          debugLog(
            `[useRealtime] Used server-side batch leave for ${threadIds.length} threads`,
          )
        } else {
          // Fallback to individual leaves but with minimal logging
          let leftCount = 0
          for (const threadId of threadIds) {
            socket.emit(SocketEvent.LEAVE_THREAD, { threadId })
            leftCount++
          }

          debugLog(
            `[useRealtime] Successfully left ${leftCount} thread rooms individually`,
          )
        }

        // Broadcast inactive threads to other tabs
        for (const threadId of threadIds) {
          broadcastToTabs({
            type: BroadcastEventType.ACTIVE_THREADS_HEARTBEAT,
            entityType: 'thread',
            entityId: threadId,
            data: { active: false, threadId, timestamp: Date.now() },
            timestamp: Date.now(),
            messageId: generateMessageId(),
            source: 'socket',
            tabId: TAB_ID,
          })
        }

        return true
      } catch (error) {
        logger.error(`[useRealtime] Error batch leaving thread rooms: ${error}`)
        return false
      }
    },
    [isReady, socket, broadcastToTabs],
  )

  /**
   * Update the visibility status of an entity
   */
  const setEntityVisibility = useCallback(
    (
      entityType: keyof ActiveEntityRegistry,
      entityId: string,
      isVisible: boolean,
    ): void => {
      if (isVisible) {
        visibleEntities[entityType].add(entityId)
      } else {
        visibleEntities[entityType].delete(entityId)
      }
    },
    [],
  )

  // Clean up the BroadcastChannel on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        channelRef.current.close()
        channelRef.current = null
      }
    }
  }, [])

  return {
    socket,
    isConnected,
    isReady,
    socketId, // Exposed for detecting socket reconnections
    currentOrgId: currentOrgIdRef.current,

    // Utilities
    markEventProcessed,
    hasProcessedEvent,
    addEventListener,
    broadcastToTabs,
    processSocketEvent,
    validateOrganizationMatch,
    isEntityActive: (
      entityType: keyof ActiveEntityRegistry,
      entityId: string,
    ) => activeEntities[entityType].has(entityId),
    isEntityVisible: (
      entityType: keyof ActiveEntityRegistry,
      entityId: string,
    ) => visibleEntities[entityType].has(entityId),
    setEntityVisibility,

    // Room management
    joinRoom,
    leaveRoom,
    batchJoinThreads,
    batchLeaveThreads,
  }
}
